import A from 'components/atoms/A/A';
import Card from 'components/atoms/Card/Card';
import Collapsible from 'components/atoms/Collapsible/Collapsible';
import Switch from 'components/atoms/Switch/Switch';
import Txt from 'components/atoms/Txt/Txt';
import PageHeader from 'components/molecules/PageHeader/PageHeader';
import React, { useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import type { PrivacySettingsQmeeQuery as PrivacySettingsQmeeQueryType } from './__generated__/PrivacySettingsQmeeQuery.graphql';
import type { PrivacySettingsQmeeMutation as PrivacySettingsQmeeMutationType } from './__generated__/PrivacySettingsQmeeMutation.graphql';
import classes from './PrivacySettings.qmee.module.scss';

const privacySettingsQuery = graphql`
  query PrivacySettingsQmeeQuery {
    consentSettings(serviceProvider: QMEE) {
      edges {
        cursor
        node {
          id
          description {
            title
            subtitle
          }
          href
          userResponse {
            answerKeys
            label
          }
        }
      }
    }
  }
`;

const privacySettingsMutation = graphql`
  mutation PrivacySettingsQmeeMutation($input: UpdateConsentSettingsInput) {
    updateConsentSettings(input: $input) {
      edges {
        cursor
        node {
          id
          description {
            title
            subtitle
          }
          href
          userResponse {
            answerKeys
            label
          }
        }
      }
    }
  }
`;

export function isToggleOn(answerKeys: ReadonlyArray<string | null> | null | undefined): boolean {
  if (!answerKeys) return false;
  return answerKeys.some((key) => key?.toLowerCase() === 'true');
}

function AdvertisingEffectivenessLearnMore() {
  return (
    <div className={classes.learnMoreContent}>
      <Txt component="p">
        We will use personal data you provide to us, such as an obscured, non-readable version of
        your email address or device identifier (e.g., mobile phone advertising ID) using hashing or
        encryption that makes it non-readable to that partner, mobile phone number, your IP address,
        or name and postal address, in a secure direct matching process with our partners (full list
        of partners below). Our partners will determine if you use any of their services (for
        example social networks, websites, mobile app) and if you are a user of both our service and
        theirs, our partners will then identify what advertisements you may have been exposed to on
        these sites and platforms and will share this information with us. We use this information
        to invite you to participate in relevant surveys. We then analyse this data to measure how
        brand attitudes and/or sales have been impacted by your exposure to these ads. These
        partners will not use the data we provide in the matching process for any other purpose
        other than delivering us the advertising exposure information. You can withdraw your consent
        at any time in your Privacy Settings.
      </Txt>

      <p className={classes.learnMoreSubheading}>
        <Txt>Data that may be shared by Qmee and Kantar with our partners:</Txt>
      </p>
      <Txt component="p">
        Persistent unique identifier, hashed or encrypted email address, social network details
        (such as username), cookie, IP address, mobile device/ad ID (MAID), mobile phone number or
        name and postal address.
      </Txt>

      <p className={classes.learnMoreSubheading}>
        <Txt>Data that may be shared by our partners with Qmee and Kantar:</Txt>
      </p>
      <Txt component="p">
        Individual-level data created by our partners about your exposure to selected
        advertisements.
      </Txt>

      <p className={classes.learnMoreSubheading}>
        <Txt>Partner List</Txt>
      </p>
      <Txt component="p">
        This list represents all current partners. Partners may be added to this list and an
        up-to-date list is always available to view in your Privacy Settings.
      </Txt>

      <div className={classes.partnerList}>
        <Txt skipTranslation component="p">
          US: Roku, Dish, DirecTV, Pandora, Reddit, LG Ads, Dotdash, Acast, TikTok, Midroll,
          BuzzFeed, Venn TV, Complex Media, Cadence13, Amazon, Twitch, ART19, Yahoo, Thrillist,
          Stitcher, Conde Nast, Megaphone, Adswizz, Zynga, Vevo, Tumblr, NBCUniversal, CVS, Imgur,
          Teads, Comcast, Freewheel, Padsquad, Crooked, Nativo, iHeart, Kargo, HBO Max, Yelp,
          Matterkind, Altice, Charter, Cox, Sling TV, Samba TV, IMDB, Fubo TV, LiveIntent, Viacom,
          CBS, Ansu, New York Times, PlayerWON, Hulu, Disney, Zeta, Viant, Walmart, Warner Media
        </Txt>
        <Txt skipTranslation component="p">
          UK: Reddit, Acast, TikTok, Buzzfeed, Amazon, Yahoo, Zynga, Vevo, Tumblr, Kargo
        </Txt>
        <Txt skipTranslation component="p">
          CA: Roku, Reddit, LG Ads, Amazon, Zynga, Vevo, Tumblr, Teads
        </Txt>
        <Txt skipTranslation component="p">
          AU: Acast, BuzzFeed, Amazon, Vevo, Tumblr, Teads, Kargo
        </Txt>
        <Txt skipTranslation component="p">
          FR: Acast, Amazon, Vevo, Teads
        </Txt>
        <Txt skipTranslation component="p">
          DE: Reddit, Amazon, Vevo, Teads
        </Txt>
      </div>
    </div>
  );
}

function DataMatchingLearnMore() {
  return (
    <div className={classes.learnMoreContent}>
      <Txt component="p">
        We and our partners use various technologies like cookies, tracking pixels, tags, cached
        objects, digital fingerprinting, ad and mobile device IDs to match and enrich your data.
        This allows us to better understand your interests and ensure that the surveys you receive
        are relevant to you.
      </Txt>

      <p className={classes.learnMoreSubheading}>
        <Txt>Data Collected/Processed</Txt>
      </p>
      <Txt component="p">
        Persistent unique identifier, contact details, email address, social login, cookie, IP
        address, mobile device ID, official identification number (i.e. ME number)
      </Txt>
    </div>
  );
}

function AdTargetingLearnMore() {
  return (
    <div className={classes.learnMoreContent}>
      <Txt component="p">
        Thanks to your participation in our surveys and your profile data, we can help our clients
        to improve their advertising targeting, and to create better online advertising models,
        through lookalike modelling or similar research methodologies.
      </Txt>

      <p className={classes.learnMoreSubheading}>
        <Txt>Data Collected/Processed</Txt>
      </p>
      <Txt component="p">
        Persistent unique identifier, contact details, email address, social login, cookie, IP
        address, mobile device ID, official identification number (i.e. ME number)
      </Txt>
    </div>
  );
}

function SelectedAdsLearnMore() {
  return (
    <div className={classes.learnMoreContent}>
      <Txt component="p">
        Our partners will use digital technologies such as cookies and mobile ad IDs to identify you
        when you browse the Internet, and display specific ads to you for research purposes. After
        the advertisements have been shown to you, we will invite you to share your opinion about
        them via a survey.
      </Txt>

      <p className={classes.learnMoreSubheading}>
        <Txt>Data Collected/Processed</Txt>
      </p>
      <Txt component="p">
        Persistent unique identifier, cookie, IP address, mobile device/ad ID (MAID), browsing
        activity data
      </Txt>
    </div>
  );
}

export const LEARN_MORE_CONTENT: Record<string, React.FC> = {
  'Advertising Effectiveness': AdvertisingEffectivenessLearnMore,
  'Data Matching and Enrichment': DataMatchingLearnMore,
  'Advertising Targeting and Media Buying Research': AdTargetingLearnMore,
  'Selected Ads in Your Own Environment': SelectedAdsLearnMore,
};

export function getLearnMoreContent(title: string): React.ReactNode | null {
  const Component = LEARN_MORE_CONTENT[title];
  return Component ? <Component /> : null;
}

export default function PrivacySettings() {
  const data = useLazyLoadQuery<PrivacySettingsQmeeQueryType>(privacySettingsQuery, {});
  const [commitMutation] = useMutation<PrivacySettingsQmeeMutationType>(privacySettingsMutation);
  const [mutatingSettingId, setMutatingSettingId] = useState<string | null>(null);
  const [localToggles, setLocalToggles] = useState<Record<string, boolean>>({});

  const settings = data.consentSettings?.edges ?? [];

  function handleToggle(cursor: string, newValue: boolean) {
    setLocalToggles((prev) => ({ ...prev, [cursor]: newValue }));
    setMutatingSettingId(cursor);

    const nextConsents = settings
      .map(({ cursor: c, node }) => {
        const isOn =
          c === cursor
            ? newValue
            : c in localToggles
              ? localToggles[c]
              : isToggleOn(node.userResponse?.answerKeys);
        return isOn ? c : null;
      })
      .filter((c): c is string => c !== null);

    commitMutation({
      variables: {
        input: { consents: nextConsents },
      },
      onCompleted: () => {
        setMutatingSettingId(null);
      },
      onError: () => {
        setLocalToggles((prev) => ({ ...prev, [cursor]: !newValue }));
        setMutatingSettingId(null);
      },
    });
  }

  function getCheckedState(
    cursor: string,
    answerKeys: ReadonlyArray<string | null> | null | undefined,
  ): boolean {
    if (cursor in localToggles) {
      return localToggles[cursor];
    }
    return isToggleOn(answerKeys);
  }

  return (
    <div className={classes.root}>
      <PageHeader title="Privacy Settings" backHref="/more" />

      {settings.map(({ cursor, node }) => {
        const checked = getCheckedState(cursor, node.userResponse?.answerKeys);
        const learnMoreContent = getLearnMoreContent(node.description.title);
        return (
          <Card key={cursor} className={classes.settingCard}>
            <div className={classes.settingHeader}>
              <Txt skipTranslation className={classes.settingTitle}>
                {node.description.title}
              </Txt>
              <div className={classes.toggleWrapper}>
                <Txt className={classes.toggleLabel}>{checked ? 'ON' : 'OFF'}</Txt>
                <Switch
                  checked={checked}
                  onToggle={(newValue) => handleToggle(cursor, newValue)}
                  isLoading={mutatingSettingId === cursor}
                  className={classes.switch}
                />
              </div>
            </div>
            {node.description.subtitle && (
              <Txt skipTranslation className={classes.settingDescription}>
                {node.description.subtitle}
              </Txt>
            )}
            {(node.href || learnMoreContent) && (
              <Collapsible
                title="Learn more"
                openTitle="Show less"
                closedTitle="Learn more"
                className={classes.collapsible}
                headerClassName={classes.collapsibleHeader}
                contentClassName={classes.collapsibleContent}
              >
                {learnMoreContent}
                {node.href && !learnMoreContent && (
                  <A href={node.href} className={classes.collapsibleLink}>
                    {node.href}
                  </A>
                )}
              </Collapsible>
            )}
          </Card>
        );
      })}
    </div>
  );
}
